import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { romaneioService } from '../../../../../api';
import { useOps } from '../../../../../hooks/useOps';
import { cleanCnpj } from '../../../../../utils';
import { AllOpsItem, EntregoRegistration, SearchType } from '../types';

export const entregoQueryKeys = {
  all: ['entrego'] as const,
  search: (documento: string) =>
    [...entregoQueryKeys.all, 'search', documento] as const,
};

function mapBackendToRegistration(item: {
  id: number;
  nao_aprovado_azumi: boolean;
  nome_completo: string;
  data_nascimento: string | null;
  cpf: string | null;
  cnpj: string | null;
  telefone: string;
  ops: string;
  ifood_uuid: string | null;
  ativo: boolean;
}): EntregoRegistration {
  return {
    id: item.id,
    nomeCompleto: item.nome_completo,
    dataNascimento: item.data_nascimento,
    cpf: item.cpf,
    cnpj: item.cnpj,
    telefone: item.telefone,
    ops: item.ops,
    ifoodUuid: item.ifood_uuid,
    ativo: item.ativo,
    naoAprovadoAzumi: item.nao_aprovado_azumi,
  };
}

export function useEntregoSearch() {
  const [registrations, setRegistrations] = useState<EntregoRegistration[]>([]);
  const [selectedRegistration, setSelectedRegistration] =
    useState<EntregoRegistration | null>(null);
  const [allOps, setAllOps] = useState<AllOpsItem[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const { currentOps, getCurrentOpsConfig } = useOps();
  const queryClient = useQueryClient();

  const searchMutation = useMutation({
    mutationFn: async ({
      searchType,
      searchValue,
    }: {
      searchType: SearchType;
      searchValue: string;
    }) => {
      const opsConfig = getCurrentOpsConfig();
      if (!opsConfig?.dbName) {
        throw new Error('Configuração de banco de dados não encontrada');
      }

      const cleanValue =
        searchType === 'cnpj'
          ? cleanCnpj(searchValue)
          : searchValue.replace(/\D/g, '');
      const params: {
        telefone?: string;
        cpf?: string;
        cnpj?: string;
        dbName: string;
      } = {
        dbName: opsConfig.dbName,
      };

      if (searchType === 'phone') {
        params.telefone = cleanValue;
      } else if (searchType === 'cpf') {
        params.cpf = cleanValue;
      } else if (searchType === 'cnpj') {
        params.cnpj = cleanValue;
      }

      const response = await romaneioService.searchCadastroDoc(params);
      return response.data?.data;
    },
    onSuccess: (data) => {
      if (!data?.cadastro || data.cadastro.length === 0) {
        setSearchError('Cadastro não encontrado');
        setRegistrations([]);
        setSelectedRegistration(null);
        return;
      }
      const mapped = data.cadastro.map(mapBackendToRegistration);
      setRegistrations(mapped);
      setSelectedRegistration(mapped.length === 1 ? mapped[0] : null);
      setAllOps(data.allOps || []);
      setSearchError(null);
    },
    onError: (error: Error) => {
      setSearchError(error.message || 'Erro ao buscar cadastro');
      setRegistrations([]);
      setSelectedRegistration(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ ativo }: { ativo: boolean }) => {
      if (!selectedRegistration) {
        throw new Error('Nenhum cadastro selecionado');
      }

      const opsConfig = getCurrentOpsConfig();
      if (!opsConfig?.dbName || !currentOps) {
        throw new Error('Configuração de banco de dados não encontrada');
      }

      const documento = selectedRegistration.cpf || selectedRegistration.cnpj;
      if (!documento) {
        throw new Error('CPF ou CNPJ não encontrado no cadastro');
      }

      const response = await romaneioService.updateCadastroStatus({
        documento,
        ativo,
        dbName: opsConfig.dbName,
        ops: currentOps,
      });

      return { ...response.data, ativo };
    },
    onSuccess: (data) => {
      setUpdateSuccess(true);
      setSelectedRegistration((prev) =>
        prev ? { ...prev, ativo: data.ativo } : null
      );
      setRegistrations((prev) =>
        prev.map((reg) =>
          reg.id === data.data?.id ? { ...reg, ativo: data.ativo } : reg
        )
      );
      queryClient.invalidateQueries({ queryKey: entregoQueryKeys.all });
    },
  });

  const search = useCallback(
    (searchType: SearchType, searchValue: string) => {
      setSearchError(null);
      setUpdateSuccess(false);
      setSelectedRegistration(null);
      searchMutation.mutate({ searchType, searchValue });
    },
    [searchMutation]
  );

  const updateStatus = useCallback(
    (ativo: boolean) => {
      setUpdateSuccess(false);
      updateMutation.mutate({ ativo });
    },
    [updateMutation]
  );

  const selectRegistration = useCallback(
    (registration: EntregoRegistration) => {
      setSelectedRegistration(registration);
      setUpdateSuccess(false);
    },
    []
  );

  const clearSearch = useCallback(() => {
    setRegistrations([]);
    setSelectedRegistration(null);
    setAllOps([]);
    setSearchError(null);
    setUpdateSuccess(false);
    searchMutation.reset();
    updateMutation.reset();
  }, [searchMutation, updateMutation]);

  const clearMessages = useCallback(() => {
    setSearchError(null);
    setUpdateSuccess(false);
  }, []);

  return {
    registrations,
    registration: selectedRegistration,
    allOps,
    isSearching: searchMutation.isPending,
    isUpdating: updateMutation.isPending,
    searchError,
    updateError: updateMutation.error?.message || null,
    updateSuccess,
    search,
    updateStatus,
    selectRegistration,
    clearSearch,
    clearMessages,
  };
}
